import React from "react";
import { LowestPrice, Price } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { QtyStartingAtPrice, RawPrice, VatMessage, PricePerUnit } from "@vp/vp-tokenized-fragment";
import _ from "lodash";

export enum PresentationType {
  STARTING_AT = "qtyStartingAtPrice",
  UNIT_PRICING = "pricePerUnit",
  RAW_PRICE = "rawPrice",
  QTY_FROM_PRICE = "QtyFromPrice",
  COUPON_CALLOUT = "couponCallout",
}

const getPricePresentationAsElement = (data: {
  price: LowestPrice;
  quantity: number;
  locale: string;
  presentationType: PresentationType;
  vatInclusivity: boolean;
  coupon?: string;
  hideVatMessage?: boolean;
  onCouponClick?: () => void;
}) => {
  const {
    price,
    quantity,
    locale,
    presentationType,
    vatInclusivity,
    coupon,
    hideVatMessage,
    onCouponClick,
  } = data;

  if (price.totalDiscountedPrice && price.totalListPrice) {
    const placeHolderText: string = price.pricePresentation
      ? (price.pricePresentation as { [type: string]: string })[presentationType]
      : "...";

    // This is a hashing function for the complex 'LowestPrice' type, in combination with the following properties:
    // quantity, locale, presentationType, coupon
    const keyDerivationFunc = (p: LowestPrice) => {
      const serializePrice = (price?: Price) => `${price?.taxed}|${price?.untaxed}`;
      return `${p.currency}|${p.fractionDigits}|${serializePrice(
        p.totalListPrice
      )}|${serializePrice(p.totalDiscountedPrice)}|${serializePrice(p.totalAmountSaved)}|${
        p.totalPercentageOff
      }|${serializePrice(p.unitListPrice)}|${serializePrice(p.unitDiscountedPrice)}|${
        p.pricePresentation?.rawPrice
      }|${p.pricePresentation?.pricePerUnit}|${p.pricePresentation?.qtyStartingAtPrice}|${
        p.pricePresentation?.couponCallout
      }|${quantity}|${locale}|${presentationType}|${coupon}`;
    };
    const key = keyDerivationFunc(price);

    switch (presentationType) {
      case PresentationType.STARTING_AT: {
        // eslint-disable-next-line no-case-declarations
        const quantityPriceInfo = {
          minimumFractionDigits: price.fractionDigits,
          currency: price.currency,
          vatInc: vatInclusivity,
          culture: locale,
          quantity: quantity,
          discountPrice: vatInclusivity
            ? price.totalDiscountedPrice.taxed
            : price.totalDiscountedPrice.untaxed,
          listPrice: vatInclusivity ? price.totalListPrice.taxed : price.totalListPrice.untaxed,
        };
        return (
          <QtyStartingAtPrice
            key={key}
            placeHolderText={placeHolderText}
            pricingInfo={quantityPriceInfo}
          />
        );
      }
      case PresentationType.RAW_PRICE: {
        const priceInfo = {
          listPrice: vatInclusivity ? price.totalListPrice.taxed : price.totalListPrice.untaxed,
          discountPrice: vatInclusivity
            ? price.totalDiscountedPrice.taxed
            : price.totalDiscountedPrice.untaxed,
          minimumFractionDigits: price.fractionDigits,
          currency: price.currency,
          vatInc: vatInclusivity,
          culture: locale,
          hideVatMessage,
        };
        return <RawPrice key={key} placeHolderText={placeHolderText} pricingInfo={priceInfo} />;
      }
      case PresentationType.UNIT_PRICING: {
        const useDiscountPrice = price.unitDiscountedPrice.taxed < price.unitListPrice.taxed;
        const priceToUse = useDiscountPrice ? price.unitDiscountedPrice : price.unitListPrice;
        const priceInfo = {
          unitListPrice: vatInclusivity ? priceToUse.taxed : priceToUse.untaxed,
          // Setting unitDiscountedPrice to be the same because it is required by PricePerUnit component
          unitDiscountPrice: vatInclusivity ? priceToUse.taxed : priceToUse.untaxed,
          minimumFractionDigits: price.fractionDigits,
          culture: locale,
          currency: price.currency,
          vatInc: vatInclusivity,
          hideVatMessage,
        };
        return <PricePerUnit key={key} placeHolderText={placeHolderText} pricingInfo={priceInfo} />;
      }
    }
  }
  return <></>;
};

const getVatMessageAsElement = (locale: string, vatInclusivity: boolean) => {
  return (
    <VatMessage
      placeHolderText="..."
      pricingInfo={{
        culture: locale,
        vatInc: vatInclusivity,
      }}
    />
  );
};

export { getPricePresentationAsElement, getVatMessageAsElement };
